import React, { useEffect, useState, useMemo, useRef, ChangeEvent, FocusEvent } from "react";
import { Combobox } from "@headlessui/react";
import ReactDOM from "react-dom";
import debounce from "common/debounce";
import { Link } from "react-router-dom";
import Input from "components/Input";
import clsx from "clsx";
import styles from "./style.module.scss";
import { searchCompanyAPI } from "services/api/haulpay";
import { ICompany } from "models/HaulPay";
import { FieldError } from "react-hook-form";
import BillToCompanyItem from "features/main/HaulPay/Carrier/RequestPay/Information/modules/BillToCompanyItem";
import AddCustomerModal from "features/main/HaulPay/modules/Shared/AddCustomerModal";
import { useToggle } from "usehooks-ts";
import Button from "../Button";
import useDeviceType from "../../hooks/useDeviceType";
import Icon from "@mdi/react";
import { mdiMagnify } from "@mdi/js";
import { routes } from "../../constants/routes";

interface DebtorSearchProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  onSelect: (company: ICompany) => void;
  error?: FieldError;
  label?: string | React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  inputClass?: string;
  dropdownClass?: string;
  portalDropdown?: boolean;
  showAddNewCustomer?: boolean;
  isConnectedDebtor?: boolean;
}

const DebtorSearch: React.FC<DebtorSearchProps> = ({
  name,
  value,
  onChange,
  onSelect,
  error,
  label,
  required = false,
  disabled = false,
  placeholder = "",
  className,
  inputClass,
  dropdownClass,
  portalDropdown = false,
  showAddNewCustomer = true,
  isConnectedDebtor = false
}) => {
  const [searchString, setSearchString] = useState<string | undefined>(value);
  const [filteredData, setFilteredData] = useState<ICompany[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);
  const [isAddCustomerModalOpen, toggleAddCustomerModal] = useToggle();
  const { isDesktop } = useDeviceType();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownPortalStyle, setDropdownPortalStyle] = useState<React.CSSProperties>({});

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query.trim()) {
          setFilteredData([]);
          setIsDropdownVisible(false);
          return;
        }
        setIsLoading(true);
        try {
          const results = await searchCompanyAPI(query, "", { is_connected: isConnectedDebtor });
          setFilteredData(results?.data?.results || []);
          setIsDropdownVisible(true);
        } catch (error) {
          setFilteredData([]);
          setIsDropdownVisible(false);
        } finally {
          setIsLoading(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    value && setSearchString(value);
  }, [value]);

  useEffect(() => {
    if (isSheetOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isSheetOpen]);

  useEffect(() => {
    if (!portalDropdown || !isDropdownVisible) return;

    const updatePosition = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPortalStyle({
        position: "fixed",
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        zIndex: 9999
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [portalDropdown, isDropdownVisible]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchString(inputValue);
    onChange && onChange(inputValue);
    debouncedSearch(inputValue);
  };

  const handleSelect = (company: ICompany) => {
    if (company) {
      setSearchString(company.dba);
      onSelect(company);
      setIsDropdownVisible(false);
      setIsSheetOpen(false);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.relatedTarget?.className.includes("company-btn")) {
      setIsDropdownVisible(false);
    }
  };

  if (isDesktop) {
    const portalOptions =
      portalDropdown && isDropdownVisible && dropdownPortalStyle.top != null
        ? ReactDOM.createPortal(
            <div
              style={dropdownPortalStyle}
              className={clsx(
                "rounded-base overflow-y-auto overflow-x-hidden border shadow-lg p-1 max-h-96",
                dropdownClass ?? "bg-white border-gray-200"
              )}>
              {filteredData.length > 0
                ? filteredData.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="company-btn w-full text-left select-none rounded-base outline-none"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(item)}>
                      <BillToCompanyItem company={item} />
                    </button>
                  ))
                : !isLoading && (
                    <div className={styles.noList}>
                      <p>You didn&apos;t find the company? </p>
                      <br />
                      <Link
                        to={routes.HAULPAY_CREDIT_CHECK_CREDIT}
                        className="underline text-info company-btn">
                        Click here to add a new connection
                      </Link>
                    </div>
                  )}
            </div>,
            document.body
          )
        : null;

    return (
      <>
        <div className={clsx(styles.smartInputContainer, className)} onBlur={handleBlur}>
          <Combobox
            name={name}
            value={filteredData.find((item) => item.dba === searchString) || null}
            disabled={disabled}
            onChange={handleSelect}>
            <div className={clsx(styles.smartInputWrapper, "relative")} ref={wrapperRef}>
              <Combobox.Input
                as={Input}
                value={searchString}
                name={name}
                label={label}
                onChange={handleInputChange}
                placeholder={placeholder}
                readOnly={disabled}
                error={error}
                isLoading={isLoading}
                required={required}
                inputClass={inputClass}
                isHideError
              />

              {!portalDropdown && isDropdownVisible && filteredData.length > 0 && (
                <Combobox.Options className={clsx(styles.debtorList, dropdownClass)}>
                  {filteredData.map((item) => (
                    <Combobox.Option
                      key={item.id}
                      value={item}
                      className={() => "select-none relative py-2 pr-2 rounded-base outline-none"}>
                      {() => <BillToCompanyItem key={item.id} company={item} />}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}

              {!portalDropdown && isDropdownVisible && filteredData.length === 0 && !isLoading && (
                <Combobox.Options className={clsx(styles.debtorList, dropdownClass)}>
                  <Combobox.Option
                    value={null}
                    className={() => "cursor-pointer select-none relative py-2 pr-2 outline-none"}>
                    {() => (
                      <div className={styles.noList}>
                        <p>You didn&apos;t find the company? </p>
                        <br />
                        <Link
                          to={routes.HAULPAY_CREDIT_CHECK_CREDIT}
                          className="underline text-info company-btn">
                          Click here to add a new connection
                        </Link>
                      </div>
                    )}
                  </Combobox.Option>
                </Combobox.Options>
              )}

              {/* Render error below dropdown instead of between input and options */}
              {!!error && <div className={styles.smartInputError}>{error.message}</div>}
            </div>
          </Combobox>
        </div>
        {portalOptions}
        <AddCustomerModal
          isAddModalOpen={isAddCustomerModalOpen}
          toggleAddModal={toggleAddCustomerModal}
          onSubmitCallback={(item: ICompany) => {
            handleSelect(item);
          }}
        />
      </>
    );
  }

  return (
    <>
      <Input
        name={name}
        value={searchString}
        label={label}
        error={error}
        onClick={() => setIsSheetOpen(true)}
        disabled={disabled}
        required={required}
        readOnly
      />
      {isSheetOpen &&
        ReactDOM.createPortal(
          <div className={styles.mobileSheet} style={{ zIndex: 111 }}>
            <span className="text-lg font-semibold">Search Shipper/Debtor</span>
            <div className={styles.searchShipperDebtorContainer}>
              <Input
                value={searchString}
                isLoading={isLoading}
                isShowClose
                onChange={handleInputChange}
                onClear={() => {
                  debouncedSearch.cancel();
                  setSearchString("");
                  onChange && onChange("");
                  setFilteredData([]);
                  setIsSheetOpen(false);
                }}
                prefixNode={<Icon path={mdiMagnify} className={styles.searchIcon} />}
                className="w-full"
                placeholder="Type to search..."
                inputClass={inputClass}
              />
            </div>
            <div className="flex-1 overflow-y-auto mt-3">
              {filteredData.length ? (
                filteredData.map((company) => (
                  <BillToCompanyItem
                    key={company.id}
                    company={company}
                    onSelectCompany={() => handleSelect(company)}
                  />
                ))
              ) : showAddNewCustomer ? (
                <div className="flex flex-col gap-1 mt-10 text-sm text-center text-placeholder">
                  <p>You didn&apos;t find the company? </p>
                  <a
                    onClick={() => {
                      setIsSheetOpen(false);
                      toggleAddCustomerModal();
                    }}
                    className="underline text-info">
                    Click here to add
                  </a>
                </div>
              ) : null}
            </div>
            <Button
              type="button"
              className="w-full mt-3 text-white bg-red-500 rounded-lg h-14 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
              onClick={() => {
                debouncedSearch.cancel();
                setIsSheetOpen(false);
              }}>
              Cancel
            </Button>
          </div>,
          document.body
        )}
      <AddCustomerModal
        isAddModalOpen={isAddCustomerModalOpen}
        toggleAddModal={toggleAddCustomerModal}
        onSubmitCallback={(item: ICompany) => {
          handleSelect(item);
        }}
      />
    </>
  );
};

export default DebtorSearch;
